import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { toReportExportAction } from '../../components/ReportExportButton';
import { reportsService } from '../../services/reportsService';
import { downloadReportCsv } from '../../utils/reportExport';
import FilterDrawer, { toFilterAction } from '../../components/FilterDrawer';
import useReportFilters from '../../utils/useReportFilters';
import ProductFilterFields, { PRODUCT_FILTER_DEFAULTS, toProductFilterParams } from '../../components/reports/ProductFilterFields';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import ReportStockItemLink from '../../components/reports/ReportStockItemLink';
import { cn, formatQuantity } from '../../lib/utils';
function fmtSales(n) {
    if (n === null || n === undefined || Number.isNaN(n)) return '-';
    return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function AnnualStockReport() {
    usePageTitle('Estoque anual');
    const { toastApiError } = useToast();
    const [payload, setPayload] = useState({ months: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const filters = useReportFilters(PRODUCT_FILTER_DEFAULTS);
    const buildParams = useCallback(() => toProductFilterParams(filters.applied), [filters.applied]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await reportsService.annualStock(buildParams());
                if (!cancelled) {
                    setPayload({
                        months: data.months || [],
                        rows: data.rows || [],
                    });
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o relatório' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [buildParams]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadReportCsv(reportsService.annualStockCsv(buildParams()), 'estoque-anual.csv');
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao exportar', fallback: 'Não foi possível exportar o relatório' });
        } finally {
            setExporting(false);
        }
    };

    const months = payload.months || [];

    const thBase = 'h-[56px] text-sm font-medium text-gray-500 text-center p-0';
    const tdBase = 'h-[56px] text-sm text-gray-800 p-0';
    const tdCenter = 'tabular-nums';
    const tdEnd = 'justify-end tabular-nums text-right';
    const ROW_HEIGHT = 56;
    const HEADER_SPAN_HEIGHT = ROW_HEIGHT * 2;
    const thBaseSpan2 = 'text-sm font-medium text-gray-500 text-center p-0 align-middle';
    const thInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const thInnerSpan2 = 'min-h-[112px] h-full w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const tdInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center';
    const monthGroupCols = months.length * 4;
    const totalCols = 3 + 3 + monthGroupCols;
    const MONTH_COL_WIDTH = 80;
    const monthColStyle = { minWidth: MONTH_COL_WIDTH, width: MONTH_COL_WIDTH };
    const monthGroupWidth = MONTH_COL_WIDTH * 4;

    const colWidths = [160, 320, 60, 60, 60, 60];
    const colLeft = [
        0,
        colWidths[0],
        colWidths[0] + colWidths[1],
        colWidths[0] + colWidths[1] + colWidths[2],
        colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3],
        colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4],
    ];
    const salesGroupWidth = colWidths[3] + colWidths[4] + colWidths[5];

    const stickyTopRow1 = {
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: '#f9fafb',
    };
    const stickyTopRow1WithLeft = (left, width) => ({
        ...stickyTopRow1,
        left,
        minWidth: width,
        width,
        zIndex: 50,
    });
    const stickyTopRow2 = {
        position: 'sticky',
        top: ROW_HEIGHT,
        zIndex: 45,
        background: '#f9fafb',
    };
    const stickyTopRow2WithLeft = (left, width) => ({
        ...stickyTopRow2,
        left,
        minWidth: width,
        width,
        zIndex: 55,
    });
    const stickyBody = (left, width) => ({
        position: 'sticky',
        left,
        minWidth: width,
        width,
        background: '#fff',
        zIndex: 20,
    });

    return (
        <AppLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col gap-0">
                <PageHeader
                    title="Estoque anual"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Relatórios' },
                    ]}
                    additionalActions={[toFilterAction({ count: filters.count, onClick: filters.openDrawer })]}
                    actions={[
                        toReportExportAction({
                            onExport: handleExport,
                            disabled: loading,
                            exporting,
                        }),
                    ]}
                />
                <div className="overflow-auto flex-1 min-h-0 -mx-4 md:-mx-8 -mb-8">
                    {loading && payload.rows.length === 0 ? null : (
                        <table className="w-full caption-bottom text-sm">
                            <TableHeader className="border-b-0 bg-transparent">
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead
                                        rowSpan={2}
                                        className={thBaseSpan2}
                                        style={{ ...stickyTopRow1WithLeft(colLeft[0], colWidths[0]), height: HEADER_SPAN_HEIGHT }}
                                    >
                                        <div className={cn(thInnerSpan2, 'report-first-col')}>Linha</div>
                                    </TableHead>
                                    <TableHead
                                        rowSpan={2}
                                        className={thBaseSpan2}
                                        style={{ ...stickyTopRow1WithLeft(colLeft[1], colWidths[1]), height: HEADER_SPAN_HEIGHT }}
                                    >
                                        <div className={thInnerSpan2}>Item</div>
                                    </TableHead>
                                    <TableHead
                                        rowSpan={2}
                                        className={thBaseSpan2}
                                        style={{ ...stickyTopRow1WithLeft(colLeft[2], colWidths[2]), height: HEADER_SPAN_HEIGHT }}
                                    >
                                        <div className={thInnerSpan2}>Und.</div>
                                    </TableHead>
                                    <TableHead
                                        colSpan={3}
                                        className={cn(thBase)}
                                        style={stickyTopRow1WithLeft(colLeft[3], salesGroupWidth)}
                                    >
                                        <div className={cn(thInner, 'border-r-2 border-r-gray-300')}>Vendas</div>
                                    </TableHead>
                                    {months.map((month) => (
                                        <TableHead key={month.key} colSpan={4} className={cn(thBase)} style={{ ...stickyTopRow1, minWidth: monthGroupWidth, width: monthGroupWidth }}>
                                            <div className={thInner}>{month.label}</div>
                                        </TableHead>
                                    ))}
                                </TableRow>
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead className={thBase} style={stickyTopRow2WithLeft(colLeft[3], colWidths[3])}><div className={cn(thInner, 'justify-end')}>Méd.</div></TableHead>
                                    <TableHead className={thBase} style={stickyTopRow2WithLeft(colLeft[4], colWidths[4])}><div className={cn(thInner, 'justify-end')}>Mín.</div></TableHead>
                                    <TableHead className={thBase} style={stickyTopRow2WithLeft(colLeft[5], colWidths[5])}><div className={cn(thInner, 'justify-end', 'border-r-2 border-r-gray-300')}>Máx.</div></TableHead>
                                    {months.map((month) => (
                                        <React.Fragment key={month.key}>
                                            <TableHead className={thBase} style={{ ...stickyTopRow2, ...monthColStyle }}><div className={thInner}>Estoque</div></TableHead>
                                            <TableHead className={thBase} style={{ ...stickyTopRow2, ...monthColStyle }}><div className={thInner}>Produção</div></TableHead>
                                            <TableHead className={thBase} style={{ ...stickyTopRow2, ...monthColStyle }}><div className={thInner}>Saída</div></TableHead>
                                            <TableHead className={thBase} style={{ ...stickyTopRow2, ...monthColStyle }}><div className={cn(thInner, 'justify-end')}>Vendas</div></TableHead>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payload.rows.length === 0 ? (
                                    <TableRow className="border-b-0 hover:bg-transparent">
                                        <TableCell colSpan={totalCols} className="text-center py-8 text-gray-500">
                                            Nenhum produto encontrado
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    payload.rows.map((r) => (
                                        <TableRow key={r.product_id} className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[0], colWidths[0])}><div className={cn(tdInner, 'report-first-col')}>{r.line}</div></TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[1], colWidths[1])}>
                                                <div className={tdInner}>
                                                    <ReportStockItemLink productId={r.product_id}>
                                                        {r.item}
                                                    </ReportStockItemLink>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[2], colWidths[2])}><div className={cn(tdInner, tdCenter, 'justify-center')}>{r.unit}</div></TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[3], colWidths[3])}><div className={cn(tdInner, tdEnd)}>{fmtSales(r.vendas?.avg)}</div></TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[4], colWidths[4])}><div className={cn(tdInner, tdEnd)}>{fmtSales(r.vendas?.min)}</div></TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[5], colWidths[5])}><div className={cn(tdInner, tdEnd, 'border-r-2 border-r-gray-300')}>{fmtSales(r.vendas?.max)}</div></TableCell>
                                            {months.map((month) => {
                                                const monthData = r.months?.[month.key] || {};
                                                return (
                                                    <React.Fragment key={`${r.product_id}-${month.key}`}>
                                                        <TableCell className={tdBase} style={monthColStyle}><div className={cn(tdInner, tdCenter, 'justify-center')}>{formatQuantity(monthData.estoque_inicial)}</div></TableCell>
                                                        <TableCell className={tdBase} style={monthColStyle}><div className={cn(tdInner, tdCenter, 'justify-center')}>{formatQuantity(monthData.producao)}</div></TableCell>
                                                        <TableCell className={tdBase} style={monthColStyle}><div className={cn(tdInner, tdCenter, 'justify-center')}>{formatQuantity(monthData.saida)}</div></TableCell>
                                                        <TableCell className={tdBase} style={monthColStyle}><div className={cn(tdInner, tdEnd)}>{fmtSales(monthData.vendas)}</div></TableCell>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    )}
                </div>
            </div>
            <FilterDrawer open={filters.open} onClose={filters.close} onApply={filters.apply} onClear={filters.clear}>
                <ProductFilterFields filters={filters} />
            </FilterDrawer>
        </AppLayout>
    );
}

export default AnnualStockReport;
